import React, { useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field, SelectInput } from './DialogShell';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { SYMBOL_SUBSETS, charsForSubset, codePointHex, SPECIAL_CHARACTERS, UNICODE_NAMES, getRecentSymbols, pushRecentSymbol } from '../../lib/symbols';
import { useInsert } from '../../hooks/useInsert';

const FONTS = ['(normal text)', 'Inter', 'Outfit', 'Playfair Display', 'Roboto Mono', 'Montserrat', 'Segoe UI Symbol', 'Arial'];

/** Insert Symbol dialog (Symbols + Special Characters tabs) */
export const SymbolDialog = () => {
  const { closeDialog } = useEditor();
  const ins = useInsert();
  const [tab, setTab] = useState('symbols');
  const [font, setFont] = useState('(normal text)');
  const [subset, setSubset] = useState('latin-1');
  const [sel, setSel] = useState('©');
  const [recent, setRecent] = useState(getRecentSymbols());
  const [codeInput, setCodeInput] = useState(codePointHex('©'));
  useEffect(() => setCodeInput(codePointHex(sel)), [sel]);
  const chars = useMemo(() => charsForSubset(SYMBOL_SUBSETS.find((s) => s.id === subset) || SYMBOL_SUBSETS[1]).slice(0, 420), [subset]);
  const fontFamily = font === '(normal text)' ? 'inherit' : font;

  const insert = (ch) => {
    ins.insertText(ch);
    setRecent(pushRecentSymbol(ch));
  };
  const onCode = (v) => {
    setCodeInput(v);
    const cp = parseInt(v, 16);
    if (!Number.isNaN(cp) && cp > 31 && cp < 0x10ffff) setSel(String.fromCodePoint(cp));
  };

  return (
    <WpDialog name="symbol" title="Symbol" width="w-[680px]" onCancel={closeDialog} cancelLabel="Close" extraFooter={<Button onClick={() => { insert(sel); }} data-testid="dialog-symbol-insert-button">Insert</Button>}>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 gap-1 bg-muted p-1 rounded-md h-9">
          <TabsTrigger value="symbols" className="text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="dialog-symbol-tab-symbols">Symbols</TabsTrigger>
          <TabsTrigger value="special" className="text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="dialog-symbol-tab-special">Special Characters</TabsTrigger>
        </TabsList>
        <TabsContent value="symbols" className="m-0 mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Font"><SelectInput value={font} onChange={setFont} options={FONTS} className="w-full" testId="symbol-font-select" /></Field>
            <Field label="Subset"><SelectInput value={subset} onChange={setSubset} options={SYMBOL_SUBSETS.map((s) => ({ value: s.id, label: s.name }))} className="w-full" testId="symbol-subset-select" /></Field>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(30px,1fr))] gap-px max-h-[210px] overflow-y-auto rounded-md border border-border p-1 bg-background" style={{ fontFamily }} data-testid="symbol-grid">
            {chars.map((ch) => (
              <button key={ch} type="button" title={`${UNICODE_NAMES[ch] || ''} U+${codePointHex(ch)}`} onClick={() => setSel(ch)} onDoubleClick={() => insert(ch)} className={`h-8 min-w-[30px] rounded-sm border text-[17px] leading-none flex items-center justify-center ${sel === ch ? 'border-primary bg-primary/15' : 'border-transparent hover:border-border hover:bg-muted'}`} data-testid={`symbol-cell-${codePointHex(ch)}`}>{ch}</button>
            ))}
          </div>
          {recent.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Recently used symbols</div>
              <div className="flex gap-px flex-wrap" data-testid="symbol-recent">{recent.map((ch) => <button key={ch} type="button" onClick={() => setSel(ch)} onDoubleClick={() => insert(ch)} className={`h-8 w-8 rounded-sm border text-[17px] ${sel === ch ? 'border-primary bg-primary/15' : 'border-border hover:bg-muted'}`} data-testid={`symbol-recent-${codePointHex(ch)}`}>{ch}</button>)}</div>
            </div>
          )}
          <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div className="text-sm"><span className="text-muted-foreground">Unicode name: </span><span data-testid="symbol-name">{UNICODE_NAMES[sel] || `Character ${sel}`}</span></div>
            <Field label="Character code"><Input value={codeInput} onChange={(e) => onCode(e.target.value)} className="h-8 w-24 font-mono uppercase" data-testid="symbol-code-input" /></Field>
            <Field label="from"><SelectInput value="hex" onChange={() => {}} options={[{ value: 'hex', label: 'Unicode (hex)' }]} testId="symbol-code-from" /></Field>
          </div>
        </TabsContent>
        <TabsContent value="special" className="m-0 mt-4">
          <div className="max-h-[300px] overflow-y-auto rounded-md border border-border" data-testid="symbol-special-list">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-xs text-muted-foreground"><tr><th className="text-left px-3 py-1.5 w-12">Char</th><th className="text-left px-3 py-1.5">Character</th><th className="text-left px-3 py-1.5">Shortcut key</th></tr></thead>
              <tbody>
                {SPECIAL_CHARACTERS.map((s) => (
                  <tr key={s.name} onClick={() => setSel(s.char)} onDoubleClick={() => insert(s.char)} className={`cursor-pointer border-t border-border ${sel === s.char ? 'bg-primary/10' : 'hover:bg-muted/60'}`} data-testid={`symbol-special-${s.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <td className="px-3 py-1 text-base">{s.char === '\u00ad' ? '-' : s.char.trim() === '' ? '␣' : s.char}</td><td className="px-3 py-1">{s.name}</td><td className="px-3 py-1 text-xs text-muted-foreground">{s.shortcut || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </WpDialog>
  );
};
